import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenavModule } from '@angular/material/sidenav';
import { Subject, forkJoin, takeUntil } from 'rxjs';
import { IOCService } from 'app/core/services/ioc.service';
import { IPGeolocationService } from 'app/core/services/ip-geolocation.service';
import { IPAnalysisDrawerService } from 'app/core/services/ip-analysis-drawer.service';
import { IOC, getIOCSeverity, getSeverityColor, getSeverityLabel } from 'app/core/types/ioc.types';
import { IPGeolocationSummary } from 'app/core/types/ip-geolocation.types';
import { GeolocationMapComponent } from '../geolocation-map/geolocation-map.component';

@Component({
    selector: 'app-ip-analysis-drawer',
    standalone: true,
    imports: [
        CommonModule,
        MatSidenavModule,
        MatButtonModule,
        MatCardModule,
        MatIconModule,
        MatChipsModule,
        MatProgressSpinnerModule,
        GeolocationMapComponent
    ],
    templateUrl: './ip-analysis-drawer.component.html',
    styleUrls: ['./ip-analysis-drawer.component.scss']
})
export class IPAnalysisDrawerComponent implements OnInit, OnDestroy {
    isOpen = false;
    currentIP: string | null = null;
    loading = false;
    iocResults: IOC[] = [];
    geolocation: IPGeolocationSummary | null = null;
    errorMessage = '';
    apiKeyExpired = false;

    private _unsubscribeAll: Subject<void> = new Subject<void>();

    constructor(
        public drawerService: IPAnalysisDrawerService,
        private iocService: IOCService,
        private geolocationService: IPGeolocationService,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        // Subscribe to drawer state
        this.drawerService.isOpen$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(isOpen => {
                console.log('📌 IPAnalysisDrawer: isOpen changed to:', isOpen);
                this.isOpen = isOpen;
                this.cdr.detectChanges();
            });

        // Subscribe to current IP and load data when it changes
        this.drawerService.currentIP$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(ip => {
                console.log('📌 IPAnalysisDrawer: currentIP changed to:', ip);
                this.currentIP = ip;
                if (ip) {
                    this.loadIPData(ip);
                }
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    /**
     * Load IOC and geolocation data for IP
     */
    private loadIPData(ip: string): void {
        this.loading = true;
        this.errorMessage = '';
        this.apiKeyExpired = false;
        this.iocResults = [];
        this.geolocation = null;

        // Perform both lookups in parallel
        forkJoin({
            ioc: this.iocService.lookupIOC(ip),
            geo: this.geolocationService.lookupIP(ip)
        }).subscribe({
            next: (results) => {
                console.log('✅ IPAnalysisDrawer: Received combined results:', results);
                this.loading = false;
                
                // Check if IOC lookup had an error
                if (results.ioc.error) {
                    this.apiKeyExpired = results.ioc.error.isApiKeyExpired || false;
                    if (this.apiKeyExpired) {
                        this.errorMessage = 'La clave API de Blackbox ha expirado. No se puede verificar si esta IP está en bases de datos de amenazas.';
                    } else {
                        this.errorMessage = `Error al consultar amenazas (código ${results.ioc.error.status}). No se puede verificar esta IP.`;
                    }
                }
                
                this.iocResults = results.ioc.iocs || [];
                this.geolocation = results.geo;
            },
            error: (error) => {
                this.loading = false;
                this.errorMessage = 'Error al consultar las APIs. Por favor, intenta nuevamente.';
                console.error('❌ IPAnalysisDrawer: Error loading data:', error);
            }
        });
    }

    /**
     * Close drawer
     */
    close(): void {
        console.log('🔴 IPAnalysisDrawer: close() called');
        this.drawerService.closeDrawer();
    }

    /**
     * Handle drawer state change from user interaction
     * This is called when the drawer is opened/closed by user action (e.g., dragging, clicking backdrop)
     */
    onDrawerOpenedChange(opened: boolean): void {
        console.log('📌 IPAnalysisDrawer: onDrawerOpenedChange - opened:', opened);
        if (!opened && this.isOpen) {
            // User closed the drawer manually, sync with service
            console.log('🔴 IPAnalysisDrawer: User closed drawer manually, syncing with service');
            this.drawerService.closeDrawer();
        }
    }

    /**
     * Get severity for IOC
     */
    getSeverity(ioc: IOC): 'critical' | 'high' | 'medium' | 'low' {
        return getIOCSeverity(ioc);
    }

    /**
     * Get color for severity
     */
    getSeverityColor(severity: 'critical' | 'high' | 'medium' | 'low'): string {
        return getSeverityColor(severity);
    }

    /**
     * Get label for severity
     */
    getSeverityLabel(severity: 'critical' | 'high' | 'medium' | 'low'): string {
        return getSeverityLabel(severity);
    }

    /**
     * Format date
     */
    formatDate(dateString: string): string {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleString('es-ES', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'Europe/Madrid'
            });
        } catch (e) {
            return dateString;
        }
    }

    /**
     * Get icon for threat type
     */
    getThreatIcon(threatType: string): string {
        const lowerType = threatType.toLowerCase();
        if (lowerType.includes('spam')) return 'mail';
        if (lowerType.includes('phishing')) return 'phishing';
        if (lowerType.includes('malware')) return 'bug_report';
        if (lowerType.includes('botnet')) return 'hub';
        if (lowerType.includes('scan')) return 'search';
        if (lowerType.includes('brute')) return 'lock';
        return 'warning';
    }
}
